<script lang="ts">
  import moment from "moment";
  import type { AccountApplication } from "@shared/types/AccountApplication";
  import { AccountApplicationStatus } from "@shared/types/AccountApplication";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { toSentenceCase } from "@shared/svelte/utils";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Flash from "@shared/services/flashv2.js";
  import { params } from "@roxi/routify";
  import { onMount } from "svelte";

  export let accountApplications: AccountApplication[] = [];

  const label = (accountApplication: AccountApplication) => {
    let text: string;

    switch (accountApplication.status) {
      case AccountApplicationStatus.in_progress:
      case AccountApplicationStatus.awaiting_signature:
      case AccountApplicationStatus.verification_required:
        text = "Resume application";
        break;
      default:
        text = "View application";
    }

    return text;
  };

  onMount(() => {
    if ($params.success) {
      Flash.now({
        header: `Your application for ${$params.distributor} is complete and has been submitted.`,
        message:
          "The distributor will review your application and assign you a sales rep upon approval.",
        type: "success",
      });
    }
  });

</script>

<style>
  .empty-state-container {
    height: 40vh;
  }

</style>

<div class="fizz-styles">
  <div class="fizz-surface fizz-card">
    <div
      class="fizz-flex-row-32-responsive fizz-space-between fizz-inset-vertical-16 fizz-align-content-start"
      class:fizz-inset-horizontal-8={$LayoutStore.isDesktop}
      class:fizz-inset-horizontal-4={$LayoutStore.isMobile}
      class:fizz-flex-row-32-responsive={$LayoutStore.isDesktop}
      class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      data-testid="applications-header"
    >
      <div class="fizz-flex-item">
        <div class="fizz-text-medium fizz-stack-8">
          Apply for an account with a new distributor by completing this form. If you already have
          an account with a distributor, you don't need to resubmit an application to use Provi to
          order.
        </div>

        <div class="fizz-text-small fizz-text-subdued">
          Information needed to apply: buyer and owner information, EIN, tax id, liquor license
          number and photo.
        </div>
      </div>
      <UIRouterLink stateName={"layout.apply_new"}>
        <button class="fizz-button-secondary"> New account application </button>
      </UIRouterLink>
    </div>

    {#if !accountApplications.length}
      <div
        class="fizz-bordered fizz-stack-16 fizz-flex-row fizz-justify-center empty-state-container"
        data-testid="applications-body-empty-state"
      >
        <div class="fizz-inset-horizontal-24">
          <p
            class="fizz-text-large fizz-text-strong fizz-margin-auto fizz-text-center fizz-stack-8"
          >
            No distributor applications yet
          </p>
          <p class="fizz-text-medium fizz-margin-auto fizz-text-center fizz-stack-16">
            Apply for an account with a new distributor
          </p>
          <UIRouterLink stateName={"layout.apply_new"}>
            <button class="fizz-button-secondary fizz-margin-auto fizz-button-small">
              New account application
            </button>
          </UIRouterLink>
        </div>
      </div>
    {:else}
      <div style="overflow-x: scroll;">
        <table
          class="fizz-table fizz-table-align-top"
          style="--heading-col-width: 120px"
          data-testid="account-applications-table"
        >
          <thead>
            <tr>
              <th width="25%">Distributor</th>
              <th width="25%">Created on</th>
              <th width="25%">Status</th>
              <th width="25%" />
            </tr>
          </thead>
          <tbody>
            {#each accountApplications as accountApplication}
              <tr data-testid="account_application_{accountApplication.distributor.id}">
                <td data-label="Distributor" class="fizz-nowrap">
                  {accountApplication.distributor.retailer_facing_name ||
                    accountApplication.distributor.name}
                </td>
                <td data-label="Due Date">
                  {moment(accountApplication.created_at).format("MMMM DD, YYYY")}
                </td>
                <td data-label="Status">
                  <span
                    class="fizz-badge"
                    class:fizz-surface-green={accountApplication.status ===
                      AccountApplicationStatus.submitted}
                    class:fizz-surface-yellow={accountApplication.status ===
                      AccountApplicationStatus.verification_required ||
                      accountApplication.status === AccountApplicationStatus.awaiting_signature}
                  >
                    {toSentenceCase(accountApplication.status)}
                  </span>
                </td>
                <td class="fizz-align-right">
                  <UIRouterLink
                    stateName={"layout.apply"}
                    params={{ id: accountApplication.id }}
                    buttonElement={true}
                  >
                    <button class="fizz-button fizz-button-small">
                      {label(accountApplication)}
                    </button>
                  </UIRouterLink>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</div>
